import React, { useId } from "react";

import "./ui.css";

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="m20.7 19.3-4.2-4.2a7.5 7.5 0 1 0-1.4 1.4l4.2 4.2 1.4-1.4ZM5 10.5a5.5 5.5 0 1 1 11 0 5.5 5.5 0 0 1-11 0Z" />
  </svg>
);

/**
 * Controlled toolbar for lists and tables. Search/filter state stays with the
 * consuming page; this component only provides consistent presentation.
 */
const DataToolbar = ({
  title,
  count,
  countLabel = "items",
  searchValue,
  onSearch,
  searchPlaceholder = "Search",
  searchLabel = "Search data",
  filters,
  actions,
  children,
  className = "",
  titleId,
}) => {
  const shouldShowSearch = typeof onSearch === "function" || searchValue !== undefined;
  const classes = ["rg-ui-data-toolbar", className].filter(Boolean).join(" ");

  const handleSearch = (event) => {
    if (onSearch) onSearch(event.target.value, event);
  };

  return (
    <div className={classes}>
      {(title || count !== undefined) && (
        <div className="rg-ui-data-toolbar__heading">
          {title && (
            <h2 id={titleId} className="rg-ui-data-toolbar__title">
              {title}
            </h2>
          )}
          {count !== undefined && (
            <span className="rg-ui-data-toolbar__count" aria-label={`${count} ${countLabel}`}>
              {count} {countLabel}
            </span>
          )}
        </div>
      )}

      <div className="rg-ui-data-toolbar__controls">
        {shouldShowSearch && (
          <label className="rg-ui-data-toolbar__search">
            <span className="rg-ui-visually-hidden">{searchLabel}</span>
            <span className="rg-ui-data-toolbar__search-icon">
              <SearchIcon />
            </span>
            <input
              type="search"
              value={searchValue === undefined ? "" : searchValue}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              readOnly={!onSearch}
            />
          </label>
        )}
        {filters && <div className="rg-ui-data-toolbar__filters">{filters}</div>}
        {children}
        {actions && <div className="rg-ui-data-toolbar__actions">{actions}</div>}
      </div>
    </div>
  );
};

/** Responsive, labelled surface for a table, list, chart, or custom data view. */
const DataRegion = ({
  title,
  count,
  countLabel,
  searchValue,
  onSearch,
  searchPlaceholder,
  searchLabel,
  filters,
  actions,
  toolbar,
  toolbarChildren,
  children,
  footer,
  className = "",
  bodyClassName = "",
  ariaLabel,
}) => {
  const generatedId = useId().replace(/:/g, "");
  const titleId = `rg-ui-data-region-title-${generatedId}`;
  const classes = ["rg-ui-data-region", className].filter(Boolean).join(" ");
  const bodyClasses = ["rg-ui-data-region__body", bodyClassName].filter(Boolean).join(" ");
  const hasDefaultToolbar =
    title ||
    count !== undefined ||
    searchValue !== undefined ||
    typeof onSearch === "function" ||
    filters ||
    actions ||
    toolbarChildren;

  return (
    <section
      className={classes}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
    >
      {toolbar ||
        (hasDefaultToolbar && (
          <DataToolbar
            title={title}
            titleId={titleId}
            count={count}
            countLabel={countLabel}
            searchValue={searchValue}
            onSearch={onSearch}
            searchPlaceholder={searchPlaceholder}
            searchLabel={searchLabel}
            filters={filters}
            actions={actions}
          >
            {toolbarChildren}
          </DataToolbar>
        ))}
      <div className={bodyClasses}>{children}</div>
      {footer && <div className="rg-ui-data-region__footer">{footer}</div>}
    </section>
  );
};

export { DataToolbar };
export default DataRegion;
